import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Text,
  Heading,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  InputGroup,
  InputLeftElement,
  Input,
  useDisclosure,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
  VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import PlusIcon from '../../../assets/icons/plus.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filters.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import TrashIcon from '../../../assets/icons/red-trash.svg';
import EditIcon from '../../../assets/icons/edit.svg';
import DownloadIcon from '../../../assets/icons/CSV.svg';
import { ReactComponent as DotsIcon } from '../../../assets/icons/dots.svg';

import {
  useGetAllFormTemplatesQuery,
  useDeleteFormTemplateMutation,
  useGetFormTemplateByIdQuery,
} from '../../../services';
import {
  CenteredSpinner,
  DeleteModal,
  Paginator,
  TableHeader,
  UserData,
} from '../../../components';
import { formatDate } from '../../../utils';
import { FormFieldTypes } from '../../../models';

export const FormTemplates: FC = () => {
  const [selectedFormTemplate, setSelectedFormTemplate] = useState<{
    id: number;
  }>();
  const [page, setPage] = useState(0);

  const [formTemplateId, setFormTemplateId] = useState<number>();

  const { data: formTemplateData } = useGetFormTemplateByIdQuery(
    Number(formTemplateId),
    {
      skip: !formTemplateId,
      refetchOnMountOrArgChange: true,
    }
  );

  const navigate = useNavigate();

  const { data, isLoading, isFetching } = useGetAllFormTemplatesQuery({
    page,
  });

  const [deleteFormTemplate] = useDeleteFormTemplateMutation();

  const {
    isOpen: isDeleteFormTemplateModalOpen,
    onOpen: onOpenDeleteFormTemplateModal,
    onClose: onCloseDeleteFormTemplateModal,
  } = useDisclosure();

  useEffect(() => {
    if (formTemplateData && formTemplateData.data.fields) {
      const worksheet = XLSX.utils.json_to_sheet([], { dateNF: 'yyyy-mm-dd' });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');

      // Define the headers for the template

      XLSX.utils.sheet_add_aoa(
        worksheet,
        [
          formTemplateData?.data?.fields
            .filter(f => f.type !== FormFieldTypes.separator)
            .map(field => field.title),
        ],
        {
          origin: 'A1',
        }
      );

      // Generate Excel file as a binary string
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      // Create a Blob from the binary string
      const blob = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });

      // Create a download link and click it to download the file
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${formTemplateData.data.name}.xlsx`;
      link.click();

      // Clean up the object URL after the download
      URL.revokeObjectURL(link.href);
      setFormTemplateId(undefined);
    }
  }, [formTemplateData]);

  return (
    <Box h="full">
      {isDeleteFormTemplateModalOpen && (
        <DeleteModal
          isOpen={isDeleteFormTemplateModalOpen}
          onClose={isDeleted => {
            if (isDeleted && selectedFormTemplate)
              void deleteFormTemplate(selectedFormTemplate);

            onCloseDeleteFormTemplateModal();
          }}
        />
      )}

      <VStack align="stretch">
        <HStack justify="space-between" mt="4" align="start">
          <HStack align="baseline">
            <Heading color="custom_blue.1" fontSize="2xl">
              Forms
            </Heading>
            <Box color="custom_gray.1" fontSize="sm">
              (
              {(data ? String(data?.data.totalElements) : '0').padStart(2, '0')}{' '}
              Forms)
            </Box>
          </HStack>

          <Button
            onClick={() => navigate(`new-form`)}
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
            fontWeight="semibold"
            fontSize="sm"
          >
            <Image src={PlusIcon} alt="plus icon" boxSize="5" mr="1" />
            Create New Form
          </Button>
        </HStack>

        <VStack
          align="stretch"
          bg="white"
          border="1px"
          borderColor="custom_gray.3"
          my="4"
          p="3"
          gap="3"
          borderRadius="md"
          minH="80vh"
        >
          <HStack py="2">
            <InputGroup flexBasis="45%">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input placeholder="Search" onChange={() => {}} />
            </InputGroup>

            {/* TODO: Add Filters */}
            {/* <Button
              leftIcon={<FiltersIcon />}
              color="custom_blue.1"
              fontWeight="semibold"
              fontSize="sm"
              boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
              border="1px"
              borderColor="custom_blue.3"
              bg="none"
              _hover={{ bg: 'none' }}
            >
              Filters
            </Button> */}
          </HStack>

          {isLoading || isFetching ? (
            <CenteredSpinner />
          ) : (
            <TableContainer
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              minH="70vh"
            >
              <Table variant="simple">
                <Thead bg="custom_gray.2">
                  <Tr>
                    <TableHeader title="Form Name" />
                    <TableHeader title="Last Updated At" />
                    <TableHeader title="Updated By" />
                    <TableHeader title="Actions" />
                  </Tr>
                </Thead>
                <Tbody>
                  {data?.data.content.map(
                    ({ id, name, modifiedAt, modifiedBy }) => (
                      <Tr key={id}>
                        <Td border="none">{name}</Td>

                        <Td border="none">{formatDate(modifiedAt)}</Td>

                        <Td border="none">
                          {modifiedBy ? (
                            <UserData
                              roleName={modifiedBy.role.name}
                              firstName={modifiedBy.firstName}
                              lastName={modifiedBy.lastName}
                              pictureUrl={modifiedBy.profilePictureThumbnailURL}
                            />
                          ) : (
                            '-'
                          )}
                        </Td>

                        <Td border="none">
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<DotsIcon />}
                              aria-label="Actions of form templates"
                              bg="transparent"
                            />
                            <MenuList p="0">
                              <MenuItem
                                py="3"
                                border="1px"
                                borderColor="blackAlpha.50"
                                _hover={{
                                  bg: 'custom_orange.4',
                                  color: 'custom_orange.2',
                                }}
                                onClick={() => {
                                  navigate(String(id));
                                }}
                              >
                                <HStack>
                                  <Image src={EditIcon} alt="Edit document" />
                                  <Text fontSize="sm" fontWeight="medium">
                                    Edit
                                  </Text>
                                </HStack>
                              </MenuItem>

                              <MenuItem
                                py="3"
                                border="1px"
                                borderColor="blackAlpha.50"
                                _hover={{
                                  bg: 'custom_orange.4',
                                  color: 'custom_orange.2',
                                }}
                                onClick={() => {
                                  setFormTemplateId(id);
                                }}
                              >
                                <HStack>
                                  <Image
                                    src={DownloadIcon}
                                    alt="Edit document"
                                    width={25}
                                  />
                                  <Text fontSize="sm" fontWeight="medium">
                                    Download Sheet Template
                                  </Text>
                                </HStack>
                              </MenuItem>

                              <MenuItem
                                py="3"
                                border="1px"
                                borderColor="blackAlpha.50"
                                _hover={{
                                  bg: 'custom_orange.4',
                                  color: 'custom_orange.2',
                                }}
                                onClick={() => {
                                  setSelectedFormTemplate({ id });
                                  onOpenDeleteFormTemplateModal();
                                }}
                              >
                                <HStack>
                                  <Image src={TrashIcon} alt="Delete phase" />
                                  <Text fontSize="sm" fontWeight="medium">
                                    Delete
                                  </Text>
                                </HStack>
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    )
                  )}
                </Tbody>
              </Table>

              <Paginator
                pageCount={Number(data?.data.totalPages)}
                onPageChange={page => setPage(page)}
              />
            </TableContainer>
          )}
        </VStack>
      </VStack>
    </Box>
  );
};
